/**
 * Real Estate Agency — commercial organisation layer (frontend).
 *
 * Two-axis model. The technical {@link WorkspaceRole} (owner/admin/manager/
 * standard/viewer) keeps governing SECURITY (custom claims + Firestore rules).
 * The AGENCY ROLE defined here is a SEPARATE, business axis that drives the UX,
 * the "my data" scoping and the KPI grouping — never security. A délégué can be
 * a technical `standard`; a `manager` (business) can be a technical `admin`.
 *
 * Stored as one doc per member at
 * `workspaces/{agencyWs}/agency_team_members/{uid}` and one doc per team at
 * `workspaces/{agencyWs}/agency_teams/{teamId}`. Both are written ONLY through
 * the agency-team callables (Admin SDK) — clients read but never write them,
 * exactly like the agency profile / mandates.
 */

import { Timestamp } from '@angular/fire/firestore';
import { WorkspaceMember, WorkspaceRole } from './workspace';
import type { Weekday, WorkingHoursWindow } from './agency-profile';

/**
 * Business role inside an agency. Orthogonal to {@link WorkspaceRole}.
 *  - principal: licensed holder (IPI/BIV titulaire) — runs the agency,
 *  - manager:   team lead with transversal oversight on their délégués,
 *  - delegate:  commercial négociateur — prospects, intakes mandates, runs
 *               viewings and owns their agenda,
 *  - assistant: back-office support (no commercial pipeline of their own),
 *  - external:  partner / read-mostly (accountant, occasional collaborator).
 */
export type AgencyRole = 'principal' | 'manager' | 'delegate' | 'assistant' | 'external';

export const AGENCY_ROLES: readonly AgencyRole[] = [
  'principal',
  'manager',
  'delegate',
  'assistant',
  'external',
];

/** i18n keys for agency-role labels — render through the `translate` pipe. */
export const AGENCY_ROLE_LABELS: Record<AgencyRole, string> = {
  principal: 'agency.team.role.principal',
  manager: 'agency.team.role.manager',
  delegate: 'agency.team.role.delegate',
  assistant: 'agency.team.role.assistant',
  external: 'agency.team.role.external',
};

/** i18n keys for the one-line description of each agency role. */
export const AGENCY_ROLE_HINTS: Record<AgencyRole, string> = {
  principal: 'agency.team.roleHint.principal',
  manager: 'agency.team.roleHint.manager',
  delegate: 'agency.team.roleHint.delegate',
  assistant: 'agency.team.roleHint.assistant',
  external: 'agency.team.roleHint.external',
};

/** Tailwind chip classes per agency role (mirrors the workspace-role palette). */
export const AGENCY_ROLE_CHIP: Record<AgencyRole, string> = {
  principal: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  manager: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  delegate: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  assistant: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
  external: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

export const AGENCY_ROLE_ICON: Record<AgencyRole, string> = {
  principal: 'workspace_premium',
  manager: 'supervisor_account',
  delegate: 'directions_run',
  assistant: 'support_agent',
  external: 'badge',
};

/** Roles eligible to lead a team / be set as someone's manager. */
export const MANAGER_AGENCY_ROLES: readonly AgencyRole[] = ['principal', 'manager'];

/**
 * How the commercial PIPELINE (leads / viewings / deals) is shared inside the
 * agency. The property/mandate INVENTORY is always shared across the agency;
 * this setting only governs who sees whose CRM. Enforced by Firestore rules
 * (Phase 2) + view scoping; configurable from agency settings.
 *
 *  - hybrid (default): shared inventory, PRIVATE pipeline — a délégué sees their
 *    own leads/viewings/deals; managers see their team; principals/admins see all,
 *  - private: everything is owned by the délégué (managers/principals still see all),
 *  - shared: the whole agency sees everything; "my data" is only a convenience filter.
 */
export type AgencyDataSharingModel = 'hybrid' | 'private' | 'shared';

export const AGENCY_DATA_SHARING_MODELS: readonly AgencyDataSharingModel[] = [
  'hybrid',
  'private',
  'shared',
];

export const DEFAULT_DATA_SHARING_MODEL: AgencyDataSharingModel = 'hybrid';

export const AGENCY_DATA_SHARING_LABELS: Record<AgencyDataSharingModel, string> = {
  hybrid: 'agency.team.sharing.hybrid.label',
  private: 'agency.team.sharing.private.label',
  shared: 'agency.team.sharing.shared.label',
};

export const AGENCY_DATA_SHARING_HINTS: Record<AgencyDataSharingModel, string> = {
  hybrid: 'agency.team.sharing.hybrid.hint',
  private: 'agency.team.sharing.private.hint',
  shared: 'agency.team.sharing.shared.hint',
};

/**
 * Business / commercial profile for one agency member. Separate from the
 * workspace `members` map (which holds the SECURITY role) so the org structure
 * can evolve without touching billing or custom claims.
 */
export interface AgencyTeamMember {
  /** Firestore doc id === the member's uid. */
  uid: string;
  workspaceId: string;
  agencyRole: AgencyRole;
  /** Manager (uid) this member reports to. Typically set on délégués. */
  managerId?: string;
  /** Team / branch membership. */
  teamId?: string;
  /** Free-text job title shown on cards, e.g. "Négociateur senior". */
  title?: string;
  /** Direct line shown to colleagues / on the shared agenda. */
  phone?: string;
  /** Monthly commercial objectives for this délégué (manager-set). */
  targets?: AgencyMemberTargets;
  /**
   * Personal working hours. When set, they override the agency-wide booking
   * hours for THIS agent (slot generation + conflict warnings). Same shape as
   * {@link BookingSettings.workingHours}.
   */
  workingHours?: Partial<Record<Weekday, WorkingHoursWindow[]>>;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
}

/** Monthly commercial objectives for a délégué — drives the "My day" KPIs. */
export interface AgencyMemberTargets {
  /** Mandates to bring in per month. */
  mandates?: number;
  /** Viewings to run per month. */
  viewings?: number;
  /** Deals to close per month. */
  deals?: number;
}

/**
 * A team / branch / office inside the agency. Managers lead a team; délégués
 * belong to one. Supports the multi-office (réseau multi-agences) structure.
 */
export interface AgencyTeam {
  /** Firestore doc id. */
  teamId: string;
  workspaceId: string;
  name: string;
  /** Optional branch / office label (e.g. "Bureau Bruxelles"). */
  branch?: string;
  /** Manager uids leading this team. */
  managerIds: string[];
  /** Hex accent color for the team chip. */
  color?: string;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
}

/**
 * Payload accepted by the `setAgencyMemberRole` callable. `null` clears the
 * field (the callable maps it to a Firestore field delete); omitting a key
 * leaves it untouched.
 */
export interface AgencyMemberRoleInput {
  agencyRole?: AgencyRole;
  managerId?: string | null;
  teamId?: string | null;
  title?: string | null;
  phone?: string | null;
  targets?: AgencyMemberTargets | null;
  workingHours?: Partial<Record<Weekday, WorkingHoursWindow[]>> | null;
}

/** Payload accepted by the `upsertAgencyTeam` callable. */
export interface AgencyTeamInput {
  /** Omit to create; provide to update. */
  teamId?: string;
  name: string;
  branch?: string | null;
  managerIds?: string[];
  color?: string | null;
}

// ---------------------------------------------------------------------------
// Pure helpers (testable; no Angular/Firestore deps)
// ---------------------------------------------------------------------------

/**
 * Resolve the effective agency role for a member, falling back to a sensible
 * default when no `agency_team_members` doc exists yet: the workspace owner is
 * the principal, everyone else is a délégué. This keeps the screen meaningful
 * on first run before anything has been assigned.
 *
 * @param uid Member uid.
 * @param ownerId Workspace owner uid.
 * @param doc The member's agency-team doc, if any.
 * @return The role to display / pre-select.
 */
export function resolveAgencyRole(
  uid: string,
  ownerId: string,
  doc?: Pick<AgencyTeamMember, 'agencyRole'>,
): AgencyRole {
  if (doc?.agencyRole) return doc.agencyRole;
  if (uid === ownerId) return 'principal';
  return 'delegate';
}

/**
 * Whether a workspace technical role may administer the agency organisation
 * (assign business roles, manage teams, change the sharing model). Mirrors
 * `canManageMembers` — owner/admin only — so org setup stays an admin action.
 *
 * @param role The member's technical workspace role.
 * @return True when the role can administer the org.
 */
export function canManageAgencyOrg(role: WorkspaceRole | null | undefined): boolean {
  return role === 'owner' || role === 'admin';
}

/**
 * Candidate managers: members whose effective agency role can lead a team.
 * Always includes the workspace owner. Used to populate the "reports to" and
 * "team manager" selectors.
 *
 * @param members Workspace members map (security layer).
 * @param ownerId Workspace owner uid.
 * @param docsByUid Agency-team docs keyed by uid.
 * @return Uids eligible to be a manager.
 */
export function managerCandidateUids(
  members: Record<string, WorkspaceMember> | undefined,
  ownerId: string,
  docsByUid: Record<string, AgencyTeamMember>,
): string[] {
  if (!members) return [];
  return Object.keys(members).filter((uid) =>
    MANAGER_AGENCY_ROLES.includes(resolveAgencyRole(uid, ownerId, docsByUid[uid])),
  );
}

/**
 * Index a list of agency-team docs by uid for O(1) lookup.
 *
 * @param docs Agency-team member docs.
 * @return Map keyed by uid.
 */
export function indexTeamMembersByUid(
  docs: readonly AgencyTeamMember[] | null | undefined,
): Record<string, AgencyTeamMember> {
  const out: Record<string, AgencyTeamMember> = {};
  for (const d of docs ?? []) out[d.uid] = d;
  return out;
}

/**
 * Count how many members belong to a given team.
 *
 * @param teamId Team id.
 * @param docs Agency-team member docs.
 * @return Number of members assigned to the team.
 */
export function teamMemberCount(
  teamId: string,
  docs: readonly AgencyTeamMember[] | null | undefined,
): number {
  return (docs ?? []).filter((d) => d.teamId === teamId).length;
}

// ---------------------------------------------------------------------------
// Scoping — "Mes / Mon équipe / Toute l'agence"
// ---------------------------------------------------------------------------

/** Which slice of the agency's data a screen shows. */
export type AgencyScope = 'mine' | 'team' | 'agency';

export const AGENCY_SCOPES: readonly AgencyScope[] = ['mine', 'team', 'agency'];

export const AGENCY_SCOPE_LABELS: Record<AgencyScope, string> = {
  mine: 'agency.scope.mine',
  team: 'agency.scope.team',
  agency: 'agency.scope.agency',
};

export const AGENCY_SCOPE_ICON: Record<AgencyScope, string> = {
  mine: 'person',
  team: 'groups',
  agency: 'corporate_fare',
};

/**
 * The scopes a user may choose on a given screen.
 *
 * @param fullReach Owner/admin or business principal — sees everything.
 * @param teamReach Business manager — sees their team.
 * @param model The agency data-sharing model (governs PIPELINE privacy).
 * @param inventory True for shared-inventory screens (properties/mandates),
 *   which stay agency-wide whatever the model.
 * @return Ordered allowed scopes (length 1 ⇒ the selector hides itself).
 */
export function availableScopes(
  fullReach: boolean,
  teamReach: boolean,
  model: AgencyDataSharingModel,
  inventory: boolean,
): AgencyScope[] {
  if (fullReach) return ['mine', 'team', 'agency'];
  if (teamReach) {
    return inventory || model === 'shared'
      ? ['mine', 'team', 'agency']
      : ['mine', 'team'];
  }
  // delegate / assistant / external
  return inventory || model === 'shared' ? ['mine', 'agency'] : ['mine'];
}

/**
 * The scope a screen opens on. Inventory screens default to the whole agency
 * (shared inventory); pipeline screens default to the user's natural reach
 * (full → agency, team → team, otherwise → their own).
 */
export function defaultScope(
  fullReach: boolean,
  teamReach: boolean,
  inventory: boolean,
): AgencyScope {
  if (inventory) return 'agency';
  if (fullReach) return 'agency';
  if (teamReach) return 'team';
  return 'mine';
}

/**
 * The set of agent uids visible under a scope, or `null` for "everyone" (no
 * filtering). "team" = members of my team + my direct reports + members of any
 * team I manage + me.
 *
 * @return Set of uids, or null when the scope imposes no agent filter.
 */
export function computeVisibleAgentIds(
  scope: AgencyScope,
  myUid: string,
  myTeamId: string | undefined,
  teamMembers: readonly AgencyTeamMember[] | null | undefined,
  teams: readonly AgencyTeam[] | null | undefined,
): Set<string> | null {
  if (scope === 'agency') return null;
  if (scope === 'mine') return new Set(myUid ? [myUid] : []);
  // team
  const ids = new Set<string>(myUid ? [myUid] : []);
  const managedTeamIds = new Set(
    (teams ?? [])
      .filter((t) => (t.managerIds ?? []).includes(myUid))
      .map((t) => t.teamId),
  );
  for (const m of teamMembers ?? []) {
    if (myTeamId && m.teamId === myTeamId) ids.add(m.uid);
    if (m.managerId === myUid) ids.add(m.uid);
    if (m.teamId && managedTeamIds.has(m.teamId)) ids.add(m.uid);
  }
  return ids;
}

/** Whether an item with the given assigned agent passes the visible set. */
export function isAgentVisible(
  assignedAgentId: string | undefined,
  visible: Set<string> | null,
): boolean {
  if (visible === null) return true;
  return !!assignedAgentId && visible.has(assignedAgentId);
}
